import { supabase } from '@/lib/supabaseClient';

/**
 * Returns the tenant_id associated with the currently authenticated user.
 * Uses saas_tenant_users table to resolve the relationship.
 */
export const getTenantId = async (): Promise<string | null> => {
  const { data: userData, error: userError } = await supabase.auth.getUser();

  if (userError || !userData?.user) {
    console.warn('[getTenantId] No authenticated user found.');
    return null;
  }

  // Check if this user is a Super Admin
  const { data: sa } = await supabase
    .from('saas_super_admins')
    .select('id')
    .eq('user_id', userData.user.id)
    .maybeSingle();

  if (sa) {
    // SA can impersonate a tenant via localStorage
    const impersonated = localStorage.getItem('impersonated_tenant_id');
    if (impersonated) return impersonated;
    // SA without impersonation — no tenant context, return null silently
    return null;
  }

  // Regular tenant user — resolve via saas_tenant_users
  const { data, error } = await supabase
    .from('saas_tenant_users')
    .select('tenant_id')
    .eq('user_id', userData.user.id)
    .maybeSingle();

  if (error) {
    console.error('[getTenantId] Error fetching tenant:', error.message);
    return null;
  }

  return data?.tenant_id ?? null;
};

/**
 * Returns the full tenant record for the currently authenticated user.
 */
export const getTenant = async () => {
  const tenantId = await getTenantId();
  if (!tenantId) return null;

  const { data, error } = await supabase
    .from('saas_tenants')
    .select('*')
    .eq('id', tenantId)
    .maybeSingle();

  if (error) {
    console.error('[getTenant] Error fetching tenant data:', error.message);
    return null;
  }

  return data;
};
